import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stethoscope, ArrowLeft, Calendar as CalendarIcon, Plus, Trash2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CalendarPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedScript, setSelectedScript] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [platform, setPlatform] = useState('Instagram');

  useEffect(() => {
    fetchData();
  }, [doctorId]);

  const fetchData = async () => {
    try {
      const [eventsRes, scriptsRes] = await Promise.all([
        axios.get(`${API}/calendar/events/${doctorId}`),
        axios.get(`${API}/scripts/${doctorId}`)
      ]);
      setEvents(eventsRes.data.events || []);
      setScripts(scriptsRes.data.scripts || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async () => {
    if (!selectedScript || !scheduledDate) {
      toast.error('Please fill all fields');
      return;
    }

    const script = scripts.find(s => s.id === selectedScript);
    if (!script) return;

    try {
      await axios.post(`${API}/calendar/events`, {
        doctor_id: doctorId,
        script_id: script.id,
        script_topic: script.topic,
        scheduled_date: scheduledDate,
        platform
      });

      toast.success('Content scheduled successfully!');
      setDialogOpen(false);
      setSelectedScript('');
      setScheduledDate('');
      setPlatform('Instagram');
      fetchData();
    } catch (error) {
      console.error('Error scheduling:', error);
      toast.error('Failed to schedule content');
    }
  };

  const handleComplete = async (eventId) => {
    try {
      await axios.put(`${API}/calendar/events/${eventId}?status=completed`);
      toast.success('Marked as completed!');
      fetchData();
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event');
    }
  };

  const handleDelete = async (eventId) => {
    try {
      await axios.delete(`${API}/calendar/events/${eventId}`);
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const getPlatformColor = (platform) => {
    const colors = {
      'Instagram': 'bg-pink-100 text-pink-700',
      'YouTube': 'bg-red-100 text-red-700',
      'LinkedIn': 'bg-blue-100 text-blue-700',
      'Facebook': 'bg-blue-100 text-blue-700',
      'Twitter': 'bg-sky-100 text-sky-700'
    };
    return colors[platform] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2C5F44] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5C7063]">Loading calendar...</p>
        </div>
      </div>
    );
  }

  const upcomingEvents = events.filter(e => e.status === 'scheduled');
  const completedEvents = events.filter(e => e.status === 'completed');

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(`/dashboard/${doctorId}`)}
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="calendar-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Stethoscope className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            <span className="text-2xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Content Calendar
            </span>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
                data-testid="calendar-schedule-btn"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Schedule Content
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-3xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#1C2A21]" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Schedule Content
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="script">Select Script</Label>
                  <Select value={selectedScript} onValueChange={setSelectedScript}>
                    <SelectTrigger data-testid="calendar-script-select">
                      <SelectValue placeholder="Choose a script" />
                    </SelectTrigger>
                    <SelectContent>
                      {scripts.map((script) => (
                        <SelectItem key={script.id} value={script.id}>
                          {script.topic}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="date">Scheduled Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="rounded-xl"
                    data-testid="calendar-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="platform">Platform</Label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger data-testid="calendar-platform-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Instagram">Instagram</SelectItem>
                      <SelectItem value="YouTube">YouTube</SelectItem>
                      <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                      <SelectItem value="Facebook">Facebook</SelectItem>
                      <SelectItem value="Twitter">Twitter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleSchedule}
                  className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full w-full"
                  data-testid="calendar-submit-btn"
                >
                  Schedule Post
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8">
          <h1 className="text-4xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Your Content Calendar
          </h1>
          <p className="text-lg text-[#5C7063]">
            {upcomingEvents.length} upcoming post{upcomingEvents.length !== 1 ? 's' : ''}
          </p>
        </motion.div>

        {/* Upcoming Events */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-[#1C2A21] mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Upcoming Posts
          </h2>
          {upcomingEvents.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-[#2C5F44]/10">
              <CalendarIcon className="w-16 h-16 text-[#2C5F44] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-lg text-[#5C7063]">No scheduled posts yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {upcomingEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="bg-white rounded-3xl p-6 card-hover border border-[#2C5F44]/10"
                  data-testid={`calendar-event-${index}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                        {event.script_topic}
                      </h3>
                      <p className="text-sm text-[#5C7063]">
                        {new Date(event.scheduled_date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPlatformColor(event.platform)}`}>
                      {event.platform}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleComplete(event.id)}
                      size="sm"
                      className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full flex-1"
                      data-testid={`calendar-complete-${index}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      Complete
                    </Button>
                    <Button
                      onClick={() => handleDelete(event.id)}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-[#2C5F44]/20"
                      data-testid={`calendar-delete-${index}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Completed Events */}
        {completedEvents.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-[#1C2A21] mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Completed Posts
            </h2>
            <div className="space-y-4">
              {completedEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="bg-white rounded-2xl p-4 border border-[#2C5F44]/10 opacity-60"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-[#1C2A21]">{event.script_topic}</h3>
                      <p className="text-sm text-[#5C7063]">
                        {new Date(event.scheduled_date).toLocaleDateString()} • {event.platform}
                      </p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}